/**
 * Shared approval-date formatter.
 *
 * Spec 2026-09-21 §6.4: render `approval_date` at its `approval_date_precision`:
 *   year  → "2004"
 *   month → "Dec 2021"
 *   day   → locale date (today's behaviour)
 * Missing/unknown precision (e.g. an old cached response) falls back to the
 * day behaviour, which is what every caller did before precision existed.
 *
 * Parses the Y-M-D prefix itself instead of handing the raw string to
 * `new Date()`: a date-only ISO string ("1996-12-17") is parsed by the
 * `Date` constructor as UTC midnight, which renders as the previous day in
 * every timezone west of GMT (e.g. America/Los_Angeles). Constructing the
 * `Date` from local year/month/day parts avoids that shift entirely.
 */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function formatApprovalDate(
  date: string | null | undefined,
  precision?: string | null,
): string | null {
  if (!date) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);
  if (!m) return date;
  // Capture groups are guaranteed present by the regex (3 groups, matched),
  // but `noUncheckedIndexedAccess` doesn't know that — narrow explicitly.
  const [, yStr, moStr, dStr] = m;
  if (!yStr || !moStr || !dStr) return date;
  const y = Number(yStr);
  const mo = Number(moStr);
  const d = Number(dStr);
  if (precision === 'year') return yStr;
  if (precision === 'month') return `${MONTHS[mo - 1] ?? moStr} ${yStr}`;
  return new Date(y, mo - 1, d).toLocaleDateString(); // 'day' or missing (old cached payloads)
}

/** The approval year as a number, parsed from the Y-M-D string prefix.
 *  Never `new Date(...).getFullYear()`: that parses a date-only string as
 *  UTC midnight and rolls Jan-1 values back a year west of GMT, and
 *  year-precision dates are stored as Jan 1. Returns null when absent or
 *  unparseable. */
export function approvalYear(date: string | null | undefined): number | null {
  if (!date) return null;
  const m = /^(\d{4})-\d{2}-\d{2}/.exec(date);
  if (!m) return null;
  const [, yStr] = m;
  if (!yStr) return null;
  return Number(yStr);
}
